import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Factory,
  Cpu,
  Ruler,
  ShieldCheck,
  ArrowRight,
  Zap,
  Sparkles,
  CheckCircle2,
  Award,
  FileText,
  Scissors,
  Wrench,
  Palette,
  PackageCheck,
  Truck,
} from "lucide-react";
import SEO from "@/components/SEO";
import { PageHeader } from "./ServicesPage";
import { BUSINESS } from "@/constants/business";

const machines = [
  {
    name: "CNC Laser Cutting Machine",
    image: "/brand/cnc-laser-cutting.png",
    tag: "Precision Cutting",
    subtitle: "Fiber laser · Metal cutting to accurate dimensions",
    description:
      "Our CNC laser cutting machine is a high-power industrial fiber laser system, engineered to slice through elevator-grade steel and stainless-steel sheets to sub-millimetre accuracy. Every cabin panel, threshold and door frame begins its life on this machine.",
    highlights: [
      "High-power fiber laser cutting head",
      "Sub-millimetre dimensional accuracy",
      "Cuts elevator-grade steel & stainless steel",
      "Automated sheet-feed for zero waste",
      "Handles cabin panels, door frames, thresholds",
    ],
  },
  {
    name: "CNC Press Brake — 220 Ton, 8+1 Axis",
    image: "/brand/cnc-press-brake.png",
    tag: "Precision Bending",
    subtitle: "220-ton force · 8+1 CNC-controlled axes",
    description:
      "A 220-ton, 8+1 axis CNC press brake — one of the most sophisticated sheet-metal bending machines in the region. It bends every elevator cabin panel, threshold and door section to exact angles, ensuring perfect assembly tolerances across every unit we ship.",
    highlights: [
      "220-Ton clamping force capacity",
      "8+1 CNC-controlled axes for compound bends",
      "Repeatable bending accuracy of ±0.02mm",
      "Handles thick structural steel sections",
      "Servo-electric back-gauge system",
    ],
  },
  {
    name: "Laser Welding & MIG Welding",
    image: "/brand/cnc-laser-welding.png",
    tag: "Precision Joining",
    subtitle: "Robotic laser welding · Zero-distortion joints",
    description:
      "Our robotic CNC laser welding station uses a focused laser beam to melt and fuse metals with pinpoint precision. This eliminates thermal distortion, produces cleaner joints, and welds a variety of metals — from mild steel structural components to stainless steel cabin skins.",
    highlights: [
      "Focused laser beam for micro-joints",
      "Minimal heat-affected zone (HAZ)",
      "Welds mild steel, stainless steel, aluminium",
      "Robotic-arm precision, repeatable seams",
      "Backed by MIG welding for structural sections",
    ],
  },
];

const steps = [
  {
    title: "Design & Engineering",
    desc: "Every order begins as a 3D CAD design. Our engineers review your building's shaft, capacity and finish requirements before releasing drawings to the shop floor.",
    stage: "CAD Design Studio",
    machine: "SolidWorks · AutoCAD",
    icon: <FileText className="w-8 h-8" />,
  },
  {
    title: "CNC Laser Cutting",
    desc: "Elevator-grade steel and stainless-steel sheets are loaded onto our fiber laser and cut to sub-millimetre accuracy — cabin panels, door frames, thresholds, controller enclosures.",
    stage: "Sheet Metal Bay",
    machine: "XT-H1530 Fiber Laser",
    icon: <Scissors className="w-8 h-8" />,
  },
  {
    title: "Press-Brake Bending",
    desc: "Cut panels move to our 220-ton, 8+1 axis CNC press brake. Every bend is programmed and verified so cabin walls, door thresholds and structural sections align perfectly at assembly.",
    stage: "Bending Line",
    machine: "220T · 8+1 Axis Accurl",
    icon: <Ruler className="w-8 h-8" />,
  },
  {
    title: "Laser & MIG Welding",
    desc: "Robotic laser welding fuses precision joints with zero thermal distortion. Heavier structural sections are TIG/MIG welded by our certified welders and inspected before hand-off.",
    stage: "Welding Cell",
    machine: "Laser Robot + MIG Station",
    icon: <Wrench className="w-8 h-8" />,
  },
  {
    title: "Finishing & Assembly",
    desc: "Cabin interiors are finished — powder-coating, mirror polish, wood veneer, or laminates as per client spec. Sub-assemblies come together with motor, controller and electricals under skilled hands.",
    stage: "Assembly Floor",
    machine: "Powder Coating · Final Fit",
    icon: <Palette className="w-8 h-8" />,
  },
  {
    title: "QC & Dispatch",
    desc: "Every finished unit passes a multi-stage quality inspection — dimensions, welds, electricals, safety devices. Only then is it crated, loaded, and dispatched to your site across South India.",
    stage: "QC Bay + Logistics",
    machine: "Multi-stage Inspection",
    icon: <PackageCheck className="w-8 h-8" />,
  },
];

export default function ManufacturingPage() {
  return (
    <>
      <SEO
        title="In-House CNC Manufacturing Facility | Vila Elevators Bangalore | Bommasandra"
        description="Vila Elevators operates a rare in-house CNC manufacturing facility in Bommasandra, Bangalore — CNC laser cutting, 220-ton 8+1 axis press brake, and laser welding. One of the very few Indian elevator companies with full in-house production."
        path="/manufacturing"
        keywords="in-house elevator manufacturing bangalore, cnc laser cutting elevator, 220 ton press brake, laser welding elevator, elevator factory bommasandra, vila manufacturing facility"
      />

      <PageHeader
        eyebrow="Manufacturing"
        title="One of the very few Indian companies with a fully in-house facility."
        desc="No third-party fabricators. No outsourced parts. From laser-cut steel to finished cabin — every component is manufactured under one roof in Bommasandra."
        image="/brand/cnc-laser-cutting.png"
      />

      {/* USP Banner */}
      <section className="relative py-16 border-b border-[#e5ddd0] overflow-hidden bg-gradient-to-r from-[#b08d57]/60 via-[#c9a674]/30 to-[#faf7f2]">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-2 border border-[#b08d57]/60 bg-[#b08d57]/10 px-4 py-2 mb-6">
                <Sparkles className="w-3 h-3 text-[#b08d57]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57]">
                  Our Biggest Differentiator
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#1a1a1a] leading-[1.05] mb-4">
                Most elevator "manufacturers" in India<br />
                are actually <span className="text-[#b08d57]">assemblers.</span>
              </h2>
              <p className="text-[#4a4a4a] text-lg leading-relaxed max-w-3xl">
                They buy parts from third-party fabricators, assemble them, and slap a brand on top. Vila is different — <strong className="text-[#1a1a1a]">we manufacture everything in-house</strong>, from laser-cutting the raw steel to bending, welding and finishing. That means better quality control, faster lead times, and true customisation.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <UspStat icon={<Factory className="w-5 h-5" />} value="100%" label="In-house production" />
              <UspStat icon={<Ruler className="w-5 h-5" />} value="±0.02mm" label="Bending accuracy" />
              <UspStat icon={<Zap className="w-5 h-5" />} value="220T" label="Press-brake force" />
              <UspStat icon={<Award className="w-5 h-5" />} value="8+1" label="CNC axes" />
            </div>
          </div>
        </div>
      </section>

      {/* Machine showcase */}
      <section data-testid="machines-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mb-20"
          >
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Our Machinery</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1a1a1a]">
              State-of-the-art CNC equipment.
            </h2>
            <p className="text-[#6b6b6b] text-lg mt-6 leading-relaxed">
              Every machine on our factory floor was chosen for one reason — precision. Because in vertical transportation, sub-millimetre tolerances aren't luxuries; they're what makes elevators safe and silent for decades.
            </p>
          </motion.div>

          <div className="space-y-24">
            {machines.map((m, i) => (
              <motion.div
                key={m.name}
                data-testid={`machine-card-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="relative">
                  <div className="border border-[#e5ddd0] bg-[#faf7f2]/40 aspect-[4/3] flex items-center justify-center p-8 overflow-hidden group">
                    <img
                      src={m.image}
                      alt={m.name}
                      className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="absolute -top-4 -left-4 bg-[#b08d57] text-white text-[10px] uppercase font-bold tracking-widest px-4 py-2">
                    Machine #{String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-3">
                    {m.tag}
                  </div>
                  <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1a1a1a] mb-3">
                    {m.name}
                  </h3>
                  <div className="text-[#b08d57] font-medium mb-6 text-sm">{m.subtitle}</div>
                  <p className="text-[#6b6b6b] text-lg leading-relaxed mb-8">{m.description}</p>
                  <ul className="space-y-3">
                    {m.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-3 text-[#4a4a4a]">
                        <CheckCircle2 className="w-5 h-5 text-[#b08d57] flex-shrink-0 mt-0.5" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It's Made — 6-step timeline */}
      <section data-testid="how-its-made-section" className="py-24 sm:py-32 bg-[#faf7f2] border-b border-[#e5ddd0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mb-16"
          >
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">How It's Made</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1a1a1a]">
              From raw steel sheet to installed elevator.
            </h2>
            <p className="text-[#6b6b6b] text-lg mt-6 leading-relaxed">
              Every Vila elevator moves through six precision stages under one roof — each one owned and quality-checked by our engineering team.
            </p>
          </motion.div>

          <div className="relative">
            {/* Vertical timeline line */}
            <div className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-4 bottom-4 w-px bg-gradient-to-b from-[#b08d57]/60 via-[#c9a674]/20 to-transparent" />

            <div className="space-y-14">
              {steps.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.05 * i }}
                  data-testid={`him-step-${i + 1}`}
                  className={`relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center ${
                    i % 2 === 1 ? "md:[&>*:first-child]:order-2 md:[&>*:first-child]:text-right" : ""
                  }`}
                >
                  {/* Step number circle on timeline */}
                  <div className="absolute left-6 md:left-1/2 md:-translate-x-1/2 -translate-y-2 w-3 h-3 bg-[#b08d57] rounded-full ring-4 ring-[#faf7f2] z-10" />

                  {/* Content side */}
                  <div className={`pl-16 md:pl-0 ${i % 2 === 1 ? "md:pr-16" : "md:pl-16"}`}>
                    <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#b08d57] mb-3">
                      Step {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#1a1a1a] mb-3">
                      {s.title}
                    </h3>
                    <p className="text-[#6b6b6b] leading-relaxed">{s.desc}</p>
                  </div>

                  {/* Visual side */}
                  <div className={`pl-16 md:pl-0 ${i % 2 === 1 ? "md:pl-16" : "md:pr-16"}`}>
                    <div className={`border border-[#e5ddd0] bg-[#faf7f2]/40 p-6 aspect-[4/3] flex flex-col justify-between hover:border-[#b08d57]/50 transition-colors ${i % 2 === 1 ? "md:text-left" : ""}`}>
                      <div className={`text-[#b08d57] ${i % 2 === 1 ? "md:ml-auto" : ""}`}>{s.icon}</div>
                      <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-[#8a8a8a] mb-1">
                          Facility Stage
                        </div>
                        <div className="text-[#1a1a1a] font-display text-xl font-bold">{s.stage}</div>
                        <div className="text-[#b08d57] text-sm mt-2">{s.machine}</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why in-house matters */}
      <section className="py-20 bg-[#faf7f2]/40 border-y border-[#e5ddd0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Why It Matters</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1a1a1a]">
              What in-house manufacturing means for your building.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <BenefitCard
              icon={<ShieldCheck />}
              title="Uncompromised Quality"
              desc="Every panel and part passes through our own QC hands — not a distant sub-contractor's."
            />
            <BenefitCard
              icon={<Cpu />}
              title="Smart Controller"
              desc="Intelligent controller + Vila web app — monitor lifts remotely, get instant fault alerts."
            />
            <BenefitCard
              icon={<Zap />}
              title="Faster Lead Times"
              desc="No waiting for third-party fabricators. We schedule, cut, bend and ship on our own timeline."
            />
            <BenefitCard
              icon={<Sparkles />}
              title="Deep Customisation"
              desc="Non-standard dimensions? Special finishes? We can adjust on the shop floor — impossible for assemblers."
            />
          </div>
        </div>
      </section>

      {/* Facility info */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">The Facility</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-6">
              Bommasandra Industrial Area, Bengaluru.
            </h2>
            <p className="text-[#6b6b6b] text-lg leading-relaxed mb-6">
              Located at {BUSINESS.address.line1}, {BUSINESS.address.line2}, {BUSINESS.address.city} — our plant sits in Bangalore's largest industrial cluster, strategically placed for logistics access across South India.
            </p>
            <div className="border border-[#e5ddd0] divide-y divide-[#e5ddd0]">
              <FactRow label="Address" value={`${BUSINESS.address.line1}, ${BUSINESS.address.line2}, ${BUSINESS.address.city} – ${BUSINESS.address.postal}`} />
              <FactRow label="Working Hours" value="Mon – Sat · 9:00 AM – 5:30 PM" />
              <FactRow label="Facility Type" value="Full-cycle CNC production plant" />
              <FactRow label="Production Coverage" value="Karnataka, AP, Telangana, Tamil Nadu" />
            </div>
          </div>
          <div className="relative">
            <img
              src="/brand/cnc-press-brake.png"
              alt="Vila Elevators manufacturing facility"
              className="w-full aspect-[4/5] object-contain border border-[#e5ddd0] bg-[#faf7f2]/40 p-6"
            />
            <div className="absolute -bottom-6 -right-6 bg-[#b08d57] p-6 hidden md:block">
              <Factory className="w-10 h-10 text-[#1a1a1a] mb-2" />
              <div className="text-[#1a1a1a] text-xs font-bold uppercase tracking-widest max-w-[140px] leading-relaxed">
                In-House<br />CNC Facility
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#faf7f2]/40 border-t border-[#e5ddd0]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-6">
            Ready to see it in person?
          </h2>
          <p className="text-[#6b6b6b] text-lg mb-10 max-w-2xl mx-auto">
            Site visits welcome — schedule a tour of our Bommasandra facility to see how Vila elevators are built from the ground up.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="manufacturing-visit-cta"
              className="inline-flex items-center gap-2 bg-[#b08d57] hover:bg-[#8a6a3d] text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors"
            >
              Schedule a Visit <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="manufacturing-call-cta"
              className="inline-flex items-center gap-2 border-2 border-[#e5ddd0] hover:border-[#b08d57] hover:text-[#b08d57] text-[#1a1a1a] px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors"
            >
              Call {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

function UspStat({ icon, value, label }) {
  return (
    <div className="border border-[#b08d57]/30 bg-[#faf7f2]/60 backdrop-blur-sm p-5">
      <div className="text-[#b08d57] mb-2">{icon}</div>
      <div className="font-display text-2xl font-black text-[#1a1a1a] leading-none">{value}</div>
      <div className="text-[10px] text-[#6b6b6b] uppercase tracking-widest mt-2 leading-tight">{label}</div>
    </div>
  );
}

function BenefitCard({ icon, title, desc }) {
  return (
    <div className="border border-[#e5ddd0] bg-[#faf7f2] p-8 hover:border-[#b08d57]/50 transition-colors">
      <div className="w-12 h-12 border border-[#b08d57]/40 text-[#b08d57] flex items-center justify-center mb-5">
        {icon}
      </div>
      <h4 className="font-display text-lg font-bold text-[#1a1a1a] mb-2">{title}</h4>
      <p className="text-[#6b6b6b] text-sm leading-relaxed">{desc}</p>
    </div>
  );
}

function FactRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 py-4 px-5">
      <div className="text-xs font-bold uppercase tracking-widest text-[#b08d57] col-span-1">{label}</div>
      <div className="text-[#4a4a4a] col-span-2 text-sm leading-relaxed">{value}</div>
    </div>
  );
}
